import React, { useEffect, useRef, useState } from 'react';

import copyText from '../utils/copy-text';
import Btn from '../display/components/Btn';
import Icon from '../display/components/Icon';

import { IGaLabel } from '../types';

export interface ICopyLinkProps {
  ariaLabel: string;
  display: {
    isMinimal: boolean;
  };
  ga: IGaLabel;
  urlPath: string;
}

function createTarget(urlPath: string): HTMLInputElement {
  const target = document.createElement('input');

  target.setAttribute('type', 'text');
  target.setAttribute('readonly', '');
  target.setAttribute('value', `${window.location.origin}${urlPath}`);

  target.style.left = '-99999px';
  target.style.position = 'fixed';
  target.style.top = '0';

  target.addEventListener('focus', event => {
    event.preventDefault();
  });

  return document.body.appendChild(target);
}

const CopyLink: React.FC<ICopyLinkProps> = ({
  ariaLabel,
  display: { isMinimal },
  ga,
  urlPath,
}) => {
  const [copied, setCopied] = useState(false);
  const btnRef = useRef<null | HTMLButtonElement>(null);

  const copyLink = (): void => {
    const target = createTarget(urlPath);
    const copyResult = copyText(target);

    if (copyResult) {
      setCopied(true);

      window.dataLayer.push({
        event: 'copyLinkEvent',
        gaAction: 'link-intent',
        gaCategory: 'share',
        gaLabel: ga.label,
      });

      setTimeout(() => {
        setCopied(false);
      }, 1500);
    }

    target.remove();
  };

  useEffect(
    () => {
      if (copied && btnRef.current) {
        btnRef.current.focus();
      }
    },
    [copied]
  );

  return (
    <>
      {isMinimal && (
        <button
          type="button"
          className={`l-align-center-children  ${
            copied ? 'has-text-teal' : 'has-text-gray has-text-hover-teal'
          }`}
          aria-label={ariaLabel}
          ref={btnRef}
          onClick={copyLink}
        >
          <span className="t-size-m has-tiny-right-marg">
            <Icon iconName="link" display={{ baseline: true }} />
          </span>{' '}
          {copied && <span className="t-size-xs">Copied!</span>}
        </button>
      )}
      {!isMinimal && (
        <Btn
          ariaLabel={ariaLabel}
          display={{
            hasOutline: true,
            isFullHeight: true,
            isFullWidth: true,
            size: 's',
            theme: 'teal',
          }}
          onClick={copyLink}
          ref={btnRef}
        >
          {copied ? 'Copied!' : 'Copy link'}
        </Btn>
      )}
    </>
  );
};

export default CopyLink;
